const API_URL = import.meta.env.VITE_API_URL ?? '/api'

const AUTH_PATHS = ['/auth/web/login', '/auth/web/signup', '/auth/logout']

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public errors?: Record<string, string[]>,
    /** Full parsed JSON body of the error response, for endpoints that return extra fields beyond `message`/`errors` (e.g. `candidatos` en desambiguación B2B). */
    public data?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Shape mínima del body del 401 cuando el servidor revocó el token con
 * motivo (SDD revocacion-con-motivo, spec sección B). `motivo`/`revocado_at`
 * ausentes → 401 default de Laravel (logout voluntario, token basura, sin
 * bearer) → el cliente NO debe mostrar nada; el silencio es un requisito.
 */
interface RevocacionBody {
  message?: string
  motivo?: string
  revocado_at?: string
}

/**
 * fc-web-app perdió la sesión única web en el Slice 1 de este SDD
 * (`webLogin()` ya no revoca `web-%` previos) → este cliente NUNCA recibe
 * `sesion_desplazada`. Solo se traduce `contrasena_cambiada`; cualquier otro
 * motivo (incluido uno nuevo que se agregue a futuro) cae al `message`
 * genérico que ya manda el servidor — evita cargar copy muerto hoy.
 */
const COPY_REVOCACION: Partial<Record<string, (fechaHora: string) => string>> = {
  contrasena_cambiada: (fechaHora) =>
    `Tu sesión se cerró: tu contraseña fue actualizada el ${fechaHora}. Si no fuiste tú, contacta a soporte.`,
}

function formatearFechaHoraLocal(revocadoAt: string): string {
  const fecha = new Date(revocadoAt)
  const dia = fecha.toLocaleDateString('es-CL')
  const hora = fecha.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
  return `${dia} a las ${hora}`
}

/** Parsea el body del 401 sin lanzar — algunas respuestas 401 pueden no traer JSON. */
async function parseRevocacionBody(res: Response): Promise<RevocacionBody | null> {
  try {
    return (await res.json()) as RevocacionBody
  } catch {
    return null
  }
}

/**
 * REQ-WEB-2/REQ-WEB-3: si el body trae `motivo`, deja el mensaje (tuteo) en
 * sessionStorage para que `Login` lo muestre al montar. Sin `motivo` → silencio
 * total, comportamiento idéntico al actual.
 *
 * NO se hace `toast.error()` acá: el logout redirige con `window.location.href`,
 * que es una navegación completa — el árbol de React se desmonta y sonner muere
 * antes de pintar. (fc-app sí puede toastear en el interceptor porque usa ruteo
 * SPA de Angular y nunca se desmonta.) Mostrarlo en /login además es mejor: el
 * mensaje aparece donde el usuario aterriza, no parpadeando durante la
 * navegación.
 *
 * `sessionStorage` (por pestaña) y no `localStorage`: el desalojo es de ESTA
 * pestaña. Sobrevive al reload porque el logout hace `removeItem` puntual del
 * token, nunca `clear()`.
 */
export const LOGOUT_MENSAJE_KEY = 'fc_logout_mensaje'

function stashMensajeSiRevocado(body: RevocacionBody | null): void {
  if (!body?.motivo) return

  const construirMensaje = COPY_REVOCACION[body.motivo]
  const mensaje =
    construirMensaje && body.revocado_at
      ? construirMensaje(formatearFechaHoraLocal(body.revocado_at))
      : body.message

  if (mensaje) sessionStorage.setItem(LOGOUT_MENSAJE_KEY, mensaje)
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = sessionStorage.getItem('auth_token')

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  const res = await fetch(`${API_URL}${path}`, { ...options, headers })

  if (res.status === 204) return undefined as T

  if (res.status === 401 && !AUTH_PATHS.includes(path)) {
    stashMensajeSiRevocado(await parseRevocacionBody(res))
    sessionStorage.removeItem('auth_token')
    window.dispatchEvent(new CustomEvent('auth:logout'))
    window.location.href = '/login'
    return undefined as T
  }

  const data = await res.json()

  if (!res.ok) {
    throw new ApiError(res.status, data.message ?? 'Error inesperado', data.errors, data)
  }

  return data as T
}

async function postForm<T>(path: string, body: FormData): Promise<T> {
  const token = sessionStorage.getItem('auth_token')

  const headers: HeadersInit = {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    // NOTE: Do NOT set Content-Type here — browser sets multipart boundary automatically
  }

  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    body,
    headers,
  })

  if (res.status === 204) return undefined as T

  if (res.status === 401 && !AUTH_PATHS.includes(path)) {
    stashMensajeSiRevocado(await parseRevocacionBody(res))
    sessionStorage.removeItem('auth_token')
    window.dispatchEvent(new CustomEvent('auth:logout'))
    window.location.href = '/login'
    return undefined as T
  }

  const data = await res.json()

  if (!res.ok) {
    throw new ApiError(res.status, data.message ?? 'Error inesperado', data.errors)
  }

  return data as T
}

interface UploadFormOptions {
  /** Called with an integer 0-100 as the browser reports upload progress. */
  onProgress?: (percent: number) => void
  /** When aborted, the returned promise rejects with a `DOMException('Aborted', 'AbortError')`. */
  signal?: AbortSignal
}

/**
 * Multipart POST via `XMLHttpRequest` instead of `fetch` — `fetch` cannot
 * report upload progress (only download progress via the response body
 * stream) and aborting a multipart POST mid-flight is awkward. XHR exposes
 * `upload.onprogress` and a native `abort()` that pairs cleanly with
 * `AbortSignal`. Mirrors `postForm`'s header/auth/error conventions.
 */
function uploadForm<T>(path: string, body: FormData, opts: UploadFormOptions = {}): Promise<T> {
  const token = sessionStorage.getItem('auth_token')

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_URL}${path}`)
    xhr.setRequestHeader('Accept', 'application/json')
    // NOTE: Do NOT set Content-Type — the browser sets the multipart boundary automatically.
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    if (opts.signal) {
      if (opts.signal.aborted) {
        xhr.abort()
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }
      opts.signal.addEventListener('abort', () => xhr.abort())
    }

    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable || !opts.onProgress) return
      opts.onProgress(Math.round((e.loaded / e.total) * 100))
    }

    xhr.onabort = () => {
      reject(new DOMException('Aborted', 'AbortError'))
    }

    xhr.onerror = () => {
      reject(new ApiError(0, 'Error de red al subir el archivo.'))
    }

    xhr.onload = () => {
      if (xhr.status === 204) {
        resolve(undefined as T)
        return
      }

      if (xhr.status === 401 && !AUTH_PATHS.includes(path)) {
        // xhr.responseText, NO res.json() — este call site usa XHR, no fetch.
        let body: RevocacionBody | null = null
        try {
          body = xhr.responseText ? (JSON.parse(xhr.responseText) as RevocacionBody) : null
        } catch {
          body = null
        }
        stashMensajeSiRevocado(body)
        sessionStorage.removeItem('auth_token')
        window.dispatchEvent(new CustomEvent('auth:logout'))
        window.location.href = '/login'
        resolve(undefined as T)
        return
      }

      let data: unknown
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : undefined
      } catch {
        reject(new ApiError(xhr.status, 'Respuesta inválida del servidor.'))
        return
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        const parsed = data as { message?: string; errors?: Record<string, string[]> } | undefined
        reject(new ApiError(xhr.status, parsed?.message ?? 'Error inesperado', parsed?.errors))
        return
      }

      resolve(data as T)
    }

    xhr.send(body)
  })
}

/** Acepta un path relativo (se le antepone `API_URL`, como siempre) O una URL
 * absoluta ya resuelta por el servidor (p. ej. `descarga_url` de
 * `ProgramaFuente`, armada con `route()` en fc-api — sdd/nuevo-programa-
 * multifuente). Anteponer `API_URL` a una URL que ya lo incluye rompería el
 * fetch con un host duplicado. */
function resolverUrlDescarga(path: string): string {
  return /^https?:\/\//i.test(path) ? path : `${API_URL}${path}`
}

async function requestBlob(path: string): Promise<Blob> {
  const token = sessionStorage.getItem('auth_token')
  const headers: HeadersInit = {
    Accept: 'application/pdf, application/octet-stream',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
  const res = await fetch(resolverUrlDescarga(path), { headers })

  if (res.status === 401) {
    stashMensajeSiRevocado(await parseRevocacionBody(res))
    sessionStorage.removeItem('auth_token')
    window.dispatchEvent(new CustomEvent('auth:logout'))
    window.location.href = '/login'
    throw new ApiError(401, 'Sesión expirada')
  }
  if (!res.ok) {
    throw new ApiError(res.status, `No se pudo descargar el archivo (${res.status})`)
  }

  return res.blob()
}

/**
 * Parsea el nombre de archivo desde un header `Content-Disposition`.
 * Soporta ambas formas: `filename="..."` (con o sin comillas) y la forma
 * RFC 5987 `filename*=UTF-8''...` (percent-encoded, usada para nombres con
 * caracteres no-ASCII) — se prioriza esta última cuando está presente.
 * Devuelve `null` si el header falta o no matchea ningún formato conocido
 * (p. ej. si el navegador lo descarta por CORS — ver `exposed_headers` en
 * `fc-api/config/cors.php`, que hoy no incluye `Content-Disposition`).
 */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].trim())
    } catch {
      // percent-encoding inválido — cae al match plano de abajo
    }
  }

  const plainMatch = header.match(/filename="?([^";]+)"?/i)
  return plainMatch ? plainMatch[1].trim() : null
}

/**
 * Igual que `requestBlob`, pero además expone el nombre de archivo sugerido
 * por el servidor vía `Content-Disposition` — usado por descargas cuyo
 * nombre depende de lógica server-side (p. ej. el ZIP de documentos, cuyo
 * nombre varía por perfil). `filename` es `null` si el header no vino o el
 * navegador no lo expuso (requests cross-origin necesitan
 * `Access-Control-Expose-Headers: Content-Disposition` en CORS) — el caller
 * debe tener un fallback.
 */
async function requestBlobWithFilename(
  path: string,
  body?: unknown,
): Promise<{ blob: Blob; filename: string | null }> {
  const token = sessionStorage.getItem('auth_token')
  // Con cuerpo se manda POST: hay descargas cuya entrada es una lista (la
  // selección múltiple de la bandeja) y por query string se va a 414 URI Too
  // Long en cuanto la selección crece.
  const esPost = body !== undefined
  const headers: HeadersInit = {
    Accept: 'application/pdf, application/octet-stream, application/zip',
    ...(esPost ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
  const res = await fetch(`${API_URL}${path}`, {
    headers,
    ...(esPost ? { method: 'POST', body: JSON.stringify(body) } : {}),
  })

  if (res.status === 401) {
    stashMensajeSiRevocado(await parseRevocacionBody(res))
    sessionStorage.removeItem('auth_token')
    window.dispatchEvent(new CustomEvent('auth:logout'))
    window.location.href = '/login'
    throw new ApiError(401, 'Sesión expirada')
  }
  if (!res.ok) {
    // Una descarga que falla puede responder JSON con el motivo real (p. ej. el
    // 422 "ningún documento tiene su PDF disponible todavía"). Mostrar ese texto
    // en vez de un código pelado es la diferencia entre que el usuario entienda
    // y que abra un ticket.
    throw new ApiError(res.status, await mensajeDeErrorDeDescarga(res))
  }

  const filename = parseContentDispositionFilename(res.headers.get('Content-Disposition'))
  const blob = await res.blob()
  return { blob, filename }
}

/** Extrae `message` si el error vino en JSON; si no, un texto genérico con el código. */
async function mensajeDeErrorDeDescarga(res: Response): Promise<string> {
  const generico = `No se pudo descargar el archivo (${res.status})`

  if (!res.headers.get('Content-Type')?.includes('application/json')) return generico

  try {
    const cuerpo = (await res.json()) as { message?: unknown }
    return typeof cuerpo.message === 'string' && cuerpo.message !== '' ? cuerpo.message : generico
  } catch {
    return generico
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
  postForm: <T>(path: string, body: FormData) => postForm<T>(path, body),
  uploadForm: <T>(path: string, body: FormData, opts?: UploadFormOptions) =>
    uploadForm<T>(path, body, opts),
  deleteJson: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  blob: (path: string) => requestBlob(path),
  blobWithFilename: (path: string) => requestBlobWithFilename(path),
  /** Descarga con cuerpo: POST + blob (selección múltiple → ZIP). */
  blobPost: (path: string, body: unknown) => requestBlobWithFilename(path, body),
}

export { ApiError }
export type { UploadFormOptions }
